import { create } from 'zustand';
import { syncManager } from '../lib/syncManager';
import { fetchUserPreferences } from '../lib/userPreferences';
import { createPreferenceSync } from '../lib/preferenceSync';
import { merge3 } from '../lib/syncDoc';
import { useCurrentView } from '../hooks/useCurrentView';
import {
  GRID_COLS, WIDGET_PAGES, addWidget as gridAdd, emptyPages, findWidget, isKnownWidget, moveWidget as gridMove,
  newWidgetId, nudgeWidget as gridNudge, resizeWidget as gridResize, sanitizeLayout, serializeLayout, withNewerFromCloud,
  type Cell, type DomainPageId, type HomePageId, type HomePages, type WidgetSize, type WidgetType,
} from '../utils/homeGrid';

/**
 * ⭐ HOME WIDGETS — which widgets sit where on Home's 7 domain pages (owner, 2026-10-05).
 *
 * One jsonb value, `user_preferences.home_widgets` (mig 0289), `{ version: 1, pages }`,
 * read once after sign-in and written WHOLE on every change. The grid rules live in
 * `utils/homeGrid` (pure); this store only decides, saves and rolls back.
 *
 * ⚠️⚠️ NOTHING WRITES BEFORE THE CLOUD COPY HAS LOADED (`ready`). Every write replaces
 * the whole value, so a widget added against the empty default would erase the saved
 * layout. A failed read leaves `ready` false (`loadFailed`): the canvas says so, and
 * nothing can be added until a retry reads it.
 * ⭐ A MISSING COLUMN (the migration not run yet) or a missing row is an EMPTY layout,
 * not an error — `select('*')` simply returns no such key. Saving then fails, and
 * that is the error the user sees: the change rolls back with SAVE_ERROR.
 *
 * ⭐ Every change is OPTIMISTIC: the canvas moves at once, the write follows, and a
 * refused write (`saveNow` → false) puts back the last CONFIRMED layout — not merely
 * the one before this change, so a burst of edits can never leave an unsaved one on
 * screen.
 *
 * ⚠️⚠️ The write is merged into the cloud's CURRENT layout, page by page
 * (lib/preferenceSync.ts): a stale tab editing one page can no longer put back
 * widgets removed elsewhere, or undo another device's change to a different page.
 * A page changed on both sides keeps this tab's copy of THIS version's widgets whole —
 * mixing two grids widget by widget could overlap them.
 *
 * ⭐⚠️ A WIDGET FROM A NEWER VERSION survives every write here (owner, 2026-10-22):
 * `sanitizeLayout` keeps an unknown type (decode AND the merge rule) with every field it
 * was stored with, and no action edits it — remove, move, nudge and resize refuse it
 * (`'newer'`), while every add, move and resize keeps off its cells (utils/homeGrid).
 * ⭐⚠️ On a page changed on both sides, its newer widgets are the CLOUD's, always
 * (`withNewerFromCloud`, review 2026-10-22): this version never edits one, so its copy is
 * only ever the one it last read — keeping it would erase, revert or bring back what a
 * newer device did. That is the release boundary: right after the next release, a tab on
 * this version is the older tab, and a window that stays focused never catches up.
 * ⚠️ Not carried: a top-level key other than `pages`, or a `version` above 1 (homeGrid).
 *
 * The page Home shows, edit mode and the highlighted widget are in-memory only: Home
 * opens on the main page on every load and sign-in, and leaving Home ends edit mode.
 */

export const SAVE_ERROR = 'Couldn’t save your layout — check your connection and try again.';
export const LOAD_ERROR = 'Couldn’t load your layout — check your connection and try again.';

/** `'newer'`: the widget is a newer version's — this version never edits it. */
export type WidgetRefusal = 'not-ready' | 'not-found' | 'no-room' | 'blocked' | 'duplicate' | 'not-offered' | 'newer';
export type Outcome<T = object> = ({ ok: true; saved: Promise<boolean> } & T) | { ok: false; reason: WidgetRefusal };

interface HomeWidgetsState {
  pages: HomePages;
  /** The cloud copy has loaded — safe to WRITE. */
  ready: boolean;
  isLoading: boolean;
  /** The read failed: the layout is unknown, never "empty". */
  loadFailed: boolean;
  initializedUserId: string | null;
  /** The last write was refused and rolled back (cleared by the next change or dismiss). */
  saveError: string | null;

  /* in-memory UI state — never persisted */
  activePage: HomePageId;
  editing: boolean;
  /** The widget to point at (just added, or "Go to it"). */
  highlightId: string | null;

  initialize: () => Promise<void>;
  clear: () => void;
  openPage: (page: HomePageId, opts?: { edit?: boolean; highlight?: string | null }) => void;
  setEditing: (on: boolean) => void;
  setHighlight: (id: string | null) => void;
  dismissSaveError: () => void;

  addWidget: (page: DomainPageId, type: WidgetType, size: WidgetSize) => Outcome<{ id: string }>;
  removeWidget: (page: DomainPageId, id: string) => Outcome;
  moveWidget: (page: DomainPageId, id: string, to: Cell) => Outcome;
  nudgeWidget: (page: DomainPageId, id: string, dx: number, dy: number) => Outcome<{ at: Cell }>;
  resizeWidget: (page: DomainPageId, id: string, size: WidgetSize) => Outcome<{ moved: boolean; at: Cell }>;
}

const UI_RESET = { activePage: 'main' as HomePageId, editing: false, highlightId: null };

export const useHomeWidgetsStore = create<HomeWidgetsState>((set, get) => {
  /* The order of the writes: only the NEWEST one failing rolls back. */
  let seq = 0;
  let inflight: Promise<void> | null = null;
  /* bumped by clear(): a read that was out when the user signed out must not land */
  let generation = 0;

  const layoutSync = createPreferenceSync<HomePages>({
    column: 'home_widgets',
    // ⭐⚠️ A newer version's widgets are the cloud's, ALWAYS — in a write, in the answer to one
    // (an edit made while it was out), and in a focus catch-up (review, 2026-10-22).
    rule: (base, local, cloud) => withNewerFromCloud(merge3({ map: 'value' }, base, local, cloud) as HomePages, cloud as HomePages),
    decode: (raw) => sanitizeLayout(raw),
    encode: serializeLayout,
    read: () => get().pages,
    adopt: (pages) => set({ pages }),
    label: 'home layout',
    // ⚠️ Only once the cloud copy is in: a failed read sets the account but holds no layout.
    loadedFor: () => (get().ready ? get().initializedUserId : null),
  });

  const commit = (next: HomePages): Promise<boolean> => {
    const mine = ++seq;
    const owner = get().initializedUserId;
    layoutSync.recordBase(get().pages);
    set({ pages: next, saveError: null });
    return layoutSync.saveNow().then(ok => {
      if (get().initializedUserId !== owner) return ok;          // signed out or switched meanwhile
      if (!ok && mine === seq) {
        // ⚠️ The NEWEST write failed: back to what the database holds — the layout
        // last loaded or confirmed (another device's pages included). An older one
        // that failed is superseded: the newer write carries every change.
        set({ pages: layoutSync.confirmed() ?? emptyPages(), saveError: SAVE_ERROR });
      }
      return ok;
    });
  };

  const load = async (): Promise<void> => {
    const gen = generation;
    const { user } = await syncManager.safeAuthCheck();
    if (!user || gen !== generation) return;
    if (get().initializedUserId === user.id && get().ready) return;
    if (get().initializedUserId !== user.id) {
      // Another account's layout must never show, or be written, while this one loads.
      seq = 0;
      layoutSync.reset();
      set({ pages: emptyPages(), ready: false, saveError: null, ...UI_RESET });
    }
    set({ isLoading: true, loadFailed: false });
    const prefs = await fetchUserPreferences();
    if (gen !== generation) return;                              // signed out meanwhile
    if (prefs && prefs.userId !== user.id) { set({ isLoading: false }); return; }
    if (!prefs || prefs.failed) {
      set({ isLoading: false, loadFailed: true, ready: false, initializedUserId: user.id });
      return;
    }
    const pages = layoutSync.load(prefs.row?.home_widgets ?? null, prefs.row?.updated_at);
    seq = 0;
    set({ pages, ready: true, isLoading: false, loadFailed: false, initializedUserId: user.id });
  };

  const edit = (page: DomainPageId, fn: (list: HomePages[DomainPageId]) => HomePages[DomainPageId] | null): { next: HomePages } | null => {
    const list = get().pages[page] ?? [];
    const changed = fn(list);
    return changed ? { next: { ...get().pages, [page]: changed } } : null;
  };

  return {
    pages: emptyPages(),
    ready: false,
    isLoading: false,
    loadFailed: false,
    initializedUserId: null,
    saveError: null,
    ...UI_RESET,

    /* ⚠️ One read at a time: `isLoading` is only set after the auth check, so two calls in
       the same tick would both pass a flag check — they share the in-flight promise instead. */
    initialize: () => {
      if (!inflight) inflight = load().finally(() => { inflight = null; });
      return inflight;
    },

    clear: () => {
      generation++;
      seq = 0;
      layoutSync.reset();
      set({
        pages: emptyPages(), ready: false, isLoading: false, loadFailed: false,
        initializedUserId: null, saveError: null, ...UI_RESET,
      });
    },

    openPage: (page, opts) => set({
      activePage: page,
      editing: page !== 'main' && Boolean(opts?.edit),
      highlightId: page !== 'main' ? opts?.highlight ?? null : null,
    }),
    setEditing: on => set(s => ({ editing: on && s.activePage !== 'main', highlightId: on ? s.highlightId : null })),
    setHighlight: id => set({ highlightId: id }),
    dismissSaveError: () => set({ saveError: null }),

    addWidget: (page, type, size) => {
      if (!get().ready) return { ok: false, reason: 'not-ready' };
      if (!WIDGET_PAGES[type]?.includes(page)) return { ok: false, reason: 'not-offered' };
      // ⭐ One of each kind: Hydration is one instrument, wherever it sits.
      if (findWidget(get().pages, type)) return { ok: false, reason: 'duplicate' };
      const id = newWidgetId();
      const r = edit(page, list => gridAdd(list, { id, type, size }, GRID_COLS));
      if (!r) return { ok: false, reason: 'no-room' };
      return { ok: true, id, saved: commit(r.next) };
    },

    removeWidget: (page, id) => {
      if (!get().ready) return { ok: false, reason: 'not-ready' };
      const list = get().pages[page] ?? [];
      const w = list.find(x => x.id === id);
      if (!w) return { ok: false, reason: 'not-found' };
      if (!isKnownWidget(w)) return { ok: false, reason: 'newer' };
      if (get().highlightId === id) set({ highlightId: null });
      return { ok: true, saved: commit({ ...get().pages, [page]: list.filter(x => x.id !== id) }) };
    },

    moveWidget: (page, id, to) => {
      if (!get().ready) return { ok: false, reason: 'not-ready' };
      const list = get().pages[page] ?? [];
      const w = list.find(x => x.id === id);
      if (!w) return { ok: false, reason: 'not-found' };
      if (!isKnownWidget(w)) return { ok: false, reason: 'newer' };
      if (w.col === to.col && w.row === to.row) return { ok: true, saved: Promise.resolve(true) };
      const moved = gridMove(list, id, to, GRID_COLS);
      if (!moved) return { ok: false, reason: 'blocked' };
      return { ok: true, saved: commit({ ...get().pages, [page]: moved }) };
    },

    nudgeWidget: (page, id, dx, dy) => {
      if (!get().ready) return { ok: false, reason: 'not-ready' };
      const list = get().pages[page] ?? [];
      const w = list.find(x => x.id === id);
      if (!w) return { ok: false, reason: 'not-found' };
      if (!isKnownWidget(w)) return { ok: false, reason: 'newer' };
      const r = gridNudge(list, id, dx, dy, GRID_COLS);
      if (!r) return { ok: false, reason: 'blocked' };
      return { ok: true, at: r.at, saved: commit({ ...get().pages, [page]: r.widgets }) };
    },

    resizeWidget: (page, id, size) => {
      if (!get().ready) return { ok: false, reason: 'not-ready' };
      const list = get().pages[page] ?? [];
      const w = list.find(x => x.id === id);
      if (!w) return { ok: false, reason: 'not-found' };
      if (!isKnownWidget(w)) return { ok: false, reason: 'newer' };
      if (w.size === size) return { ok: true, moved: false, at: { col: w.col, row: w.row }, saved: Promise.resolve(true) };
      const r = gridResize(list, id, size, GRID_COLS);
      if (r.kind === 'refused') return { ok: false, reason: 'no-room' };
      return { ok: true, moved: r.kind === 'moved', at: r.at, saved: commit({ ...get().pages, [page]: r.widgets }) };
    },
  };
});

/* ⭐ Leaving Home ends edit mode (and the highlight) — cleared by the VIEW changing, not
   by a component unmounting: StrictMode runs every effect's cleanup once on mount, which
   would end an edit an area's "Home widget" pill had just asked for. */
useCurrentView.subscribe((s, prev) => {
  if (s.currentView === prev.currentView || s.currentView === 'dashboard') return;
  const st = useHomeWidgetsStore.getState();
  if (st.editing || st.highlightId) useHomeWidgetsStore.setState({ editing: false, highlightId: null });
});
